import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PADDING = 16;

function normalizeAnchor(rawAnchor) {
  if (!rawAnchor) return null;
  if (Array.isArray(rawAnchor) && rawAnchor.length > 0) {
    const point = rawAnchor[0];
    if (point && typeof point.clientX === "number" && typeof point.clientY === "number") {
      return { x: point.clientX, y: point.clientY };
    }
  }

  const { clientX, clientY, pageX, pageY, x: rawX, y: rawY } = rawAnchor;
  const fallbackX = typeof rawX === "number" ? rawX : null;
  const fallbackY = typeof rawY === "number" ? rawY : null;
  const x =
    typeof clientX === "number"
      ? clientX
      : typeof pageX === "number"
      ? pageX
      : fallbackX;
  const y =
    typeof clientY === "number"
      ? clientY
      : typeof pageY === "number"
      ? pageY
      : fallbackY;

  if (typeof x === "number" && typeof y === "number") {
    return { x, y };
  }

  return null;
}

export default function Modal({
  open,
  onClose,
  children,
  size = "max-w-lg",
  anchor = null,
  showCloseButton = true,
  panelClassName = "",
  zIndex = 50,
}) {
  const containerRef = useRef(null);
  const [positionStyle, setPositionStyle] = useState(null);
  const [portalNode] = useState(() => {
    if (typeof document === "undefined") return null;
    const node = document.createElement("div");
    node.className = "modal-portal";
    return node;
  });

  useEffect(() => {
    if (!portalNode) return undefined;
    document.body.appendChild(portalNode);
    return () => {
      if (portalNode.parentNode) {
        portalNode.parentNode.removeChild(portalNode);
      }
    };
  }, [portalNode]);

  useLayoutEffect(() => {
    if (!open) {
      setPositionStyle(null);
      return;
    }

    const anchorPoint = normalizeAnchor(anchor);
    const node = containerRef.current;
    if (!node) return;

    const computePosition = () => {
      const rect = node.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const maxLeft = Math.max(PADDING, viewportWidth - rect.width - PADDING);
      const maxTop = Math.max(PADDING, viewportHeight - rect.height - PADDING);

      let left = Math.max(PADDING, (viewportWidth - rect.width) / 2);
      let top = Math.max(PADDING, (viewportHeight - rect.height) / 2);

      if (anchorPoint) {
        left = anchorPoint.x - rect.width / 2;
        top = anchorPoint.y - rect.height / 2;
        left = Math.min(Math.max(left, PADDING), maxLeft);
        top = Math.min(Math.max(top, PADDING), maxTop);
      } else {
        left = Math.min(left, maxLeft);
        top = Math.min(top, maxTop);
      }

      setPositionStyle({
        left,
        top,
      });
    };

    computePosition();
  }, [open, anchor, children]);

  useEffect(() => {
    if (!open) return undefined;

    const handleResize = () => {
      if (!containerRef.current) return;
      setPositionStyle(null);
      requestAnimationFrame(() => {
        if (!containerRef.current) return;
        const event = new Event("_modal_recalc");
        containerRef.current.dispatchEvent(event);
      });
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !containerRef.current) return undefined;

    const node = containerRef.current;
    const handleRecalc = () => {
      if (!open || !node) return;
      const anchorPoint = normalizeAnchor(anchor);
      const rect = node.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const maxLeft = Math.max(PADDING, viewportWidth - rect.width - PADDING);
      const maxTop = Math.max(PADDING, viewportHeight - rect.height - PADDING);

      let left = Math.max(PADDING, (viewportWidth - rect.width) / 2);
      let top = Math.max(PADDING, (viewportHeight - rect.height) / 2);

      if (anchorPoint) {
        left = anchorPoint.x - rect.width / 2;
        top = anchorPoint.y - rect.height / 2;
        left = Math.min(Math.max(left, PADDING), maxLeft);
        top = Math.min(Math.max(top, PADDING), maxTop);
      } else {
        left = Math.min(left, maxLeft);
        top = Math.min(top, maxTop);
      }

      setPositionStyle({ left, top });
    };

    node.addEventListener("_modal_recalc", handleRecalc);
    requestAnimationFrame(handleRecalc);

    return () => {
      node.removeEventListener("_modal_recalc", handleRecalc);
    };
  }, [open, anchor]);

  if (!open || !portalNode) return null;

  const anchorPoint = normalizeAnchor(anchor);

  return createPortal(
    <div
      className="fixed inset-0 bg-slate-900/60"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
    >
      <div
        ref={containerRef}
        className={`modal-surface absolute w-full ${size} transform rounded-2xl bg-white p-5 shadow-xl transition sm:p-6 ${panelClassName}`}
        style={{
          left: positionStyle?.left ?? PADDING,
          top: positionStyle?.top ?? PADDING,
          width: "calc(100% - 32px)",
          maxHeight: "calc(100dvh - 32px)",
          overflowY: "auto",
          visibility: positionStyle ? "visible" : anchorPoint ? "hidden" : "visible",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {showCloseButton ? (
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-slate-400 transition hover:text-slate-600"
            aria-label="Fechar"
          >
            &#10005;
          </button>
        ) : null}
        <div className="mt-6 space-y-6">
          {children}
        </div>
      </div>
    </div>,
    portalNode
  );
}
